import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
const systems = [{
  id: 'ducted',
  title: 'Ducted AC',
  fullTitle: 'Ducted air conditioning',
  image: 'https://cdn.magicpatterns.com/uploads/74DBnnnPaDiHoNeUuZD5vA/ducted-air-conditoning-accutemp.png',
  description: 'Discreet, hidden air conditioning for offices and commercial interiors where appearance and quiet operation matter.',
  applications: ['Premium Offices', 'Retail Fitouts', 'Hotels & Leisure'],
  href: '/services/ducted'
}, {
  id: 'cassette',
  title: 'Cassette AC',
  fullTitle: 'Cassette air conditioning',
  image: 'https://cdn.magicpatterns.com/uploads/kUteWPqLmbhAZP4Y92BuJK/cassette-detail.png',
  description: 'Ceiling-mounted system with even airflow, ideal for offices, shops and customer-facing commercial spaces.',
  applications: ['Open-plan Offices', 'Retail Units', 'Restaurants'],
  href: '/services/cassette'
}, {
  id: 'wall-mounted',
  title: 'Wall Mounted',
  fullTitle: 'Wall mounted air conditioning',
  image: 'https://cdn.magicpatterns.com/uploads/akgyqHU1pPLUBdVziFo8Cy/wall-mounted-air-conditioning.webp',
  description: 'Practical commercial solution for larger rooms and spaces where wall-mounted installation is the right fit.',
  applications: ['Server Rooms', 'Meeting Rooms', 'Individual Offices'],
  href: '/services/wall-mounted'
}];
export function SystemsWeInstall() {
  const [activeTab, setActiveTab] = useState(systems[0].id);
  const activeSystem = systems.find(s => s.id === activeTab) || systems[0];
  return <section className="py-24 bg-white overflow-hidden" data-id="element-632">
      <div className="container mx-auto px-6" data-id="element-633">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-8" data-id="element-634">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl" data-id="element-635">
            <span className="text-accent font-bold uppercase tracking-widest text-xs mb-4 block" data-id="element-636">
              System Types
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-primary-dark mb-4" data-id="element-637">
              Systems we install
            </h2>
            <p className="text-lg text-gray-600" data-id="element-638">
              High-performance commercial solutions tailored to your building's
              architecture.
            </p>
          </motion.div>

          {/* Modern Tab Switcher */}
          <div className="flex p-1.5 bg-gray-100 rounded-full w-fit" data-id="element-639">
            {systems.map(system => <button key={system.id} onClick={() => setActiveTab(system.id)} className={`relative px-6 py-2.5 rounded-full text-sm font-bold transition-colors duration-300 ${activeTab === system.id ? 'text-white' : 'text-gray-500 hover:text-primary-dark'}`} data-id="element-640">
                {activeTab === system.id && <motion.div layoutId="activeTab" className="absolute inset-0 bg-primary-dark rounded-full shadow-md" transition={{
              type: 'spring',
              duration: 0.5,
              bounce: 0.2
            }} data-id="element-641" />}
                <span className="relative z-10" data-id="element-642">{system.title}</span>
              </button>)}
          </div>
        </div>

        {/* Content Area */}
        <div className="relative min-h-[500px]" data-id="element-643">
          <AnimatePresence mode="wait" data-id="element-644">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} className="bg-gray-50 rounded-[2.5rem] overflow-hidden border border-gray-200 shadow-sm" data-id="element-645">
              <div className="grid lg:grid-cols-2" data-id="element-646">
                {/* Image Side */}
                <div className="relative h-72 lg:h-[550px] overflow-hidden" data-id="element-647">
                  <motion.img initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} transition={{
                  duration: 0.8
                }} src={activeSystem.image} alt={activeSystem.fullTitle} className="w-full h-full object-cover" data-id="element-648" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/20 to-transparent" data-id="element-649" />
                </div>

                {/* Text Side */}
                <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center" data-id="element-650">
                  <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.2
                }} data-id="element-651">
                    <h3 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6" data-id="element-652">
                      {activeSystem.fullTitle}
                    </h3>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-653">
                      {activeSystem.description}
                    </p>

                    <div className="space-y-4 mb-10" data-id="element-654">
                      <span className="text-xs font-bold text-primary-dark uppercase tracking-widest block mb-4" data-id="element-655">
                        Typical Applications
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-656">
                        {activeSystem.applications.map((app, i) => <div key={i} className="flex items-center gap-2 text-gray-700" data-id="element-657">
                            <CheckCircle2 className="w-5 h-5 text-accent shrink-0" data-id="element-658" />
                            <span className="font-medium" data-id="element-659">{app}</span>
                          </div>)}
                      </div>
                    </div>

                    <Link to={activeSystem.href} className="group inline-flex items-center gap-3 px-8 py-4 bg-primary-dark text-white font-bold rounded-full hover:bg-primary-dark/90 transition-all shadow-lg shadow-primary-dark/10" data-id="element-660">
                      View Technical Specs
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-661" />
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Site Survey Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 p-8 bg-blue-50 rounded-3xl border border-blue-100 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-662">
          <div className="max-w-2xl" data-id="element-663">
            <h4 className="text-xl font-bold text-primary-dark mb-2" data-id="element-664">
              Not sure which system is right for your building?
            </h4>
            <p className="text-gray-600" data-id="element-665">
              Our engineers assess your space during a free site survey and
              recommend the most efficient setup for your specific requirements.
            </p>
          </div>
          <Link to="/#quote" className="whitespace-nowrap px-8 py-3 bg-white text-primary-dark font-bold rounded-full border border-primary-dark/10 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-666">
            Book Site Survey
          </Link>
        </motion.div>
      </div>
    </section>;
}